import { useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"

interface PartnershipBullet {
  title?: string;
  description?: string;
}

interface PartnershipBulletsEditModalProps {
  open: boolean;
  onClose: () => void;
  bullets: PartnershipBullet[];
  onSave: (descriptions: string[]) => void;
}

export default function PartnershipBulletsEditModal({ open, onClose, bullets, onSave }: PartnershipBulletsEditModalProps) {
  const [descriptions, setDescriptions] = useState<string[]>([])
  const taRefs = useRef<Record<number, HTMLTextAreaElement>>({})

  useEffect(() => {
    if (open && Array.isArray(bullets)) {
      setDescriptions(bullets.map((b) => (typeof b?.description === "string" ? b.description : "")))
      taRefs.current = {}
    }
  }, [open, bullets])

  const wrapBold = (index: number) => {
    const el = taRefs.current[index]
    if (!el) return
    const text = descriptions[index] ?? ""
    const start = el.selectionStart
    const end = el.selectionEnd
    const sel = text.slice(start, end)
    const inner = sel.length > 0 ? sel : "bold"
    const wrapped = `**${inner}**`
    const next = text.slice(0, start) + wrapped + text.slice(end)
    setDescriptions((prev) => {
      const copy = [...prev]
      copy[index] = next
      return copy
    })
    requestAnimationFrame(() => {
      el.focus()
      const i0 = start + 2
      const i1 = start + 2 + inner.length
      el.setSelectionRange(i0, i1)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, index: number) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault()
      wrapBold(index)
    }
  }

  if (!open) return null

  const list = Array.isArray(bullets) ? bullets : []

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 border-0 cursor-default"
        aria-label="Close dialog backdrop"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-6 max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Edit partnership bullets</h3>
          <p className="text-sm text-gray-600 mt-1">Titles are fixed; edit descriptions below.</p>
          <p className="text-xs text-gray-500 mt-2">
            Use <code>**text**</code> for bold. Shortcut:{" "}
            <kbd className="px-1 bg-gray-100 rounded">Ctrl</kbd>+<kbd className="px-1 bg-gray-100 rounded">B</kbd> /{" "}
            <kbd className="px-1 bg-gray-100 rounded">⌘</kbd>+<kbd className="px-1 bg-gray-100 rounded">B</kbd> per field.
          </p>
        </div>
        <div className="px-5 py-3 flex-1 overflow-y-auto flex flex-col gap-6 min-h-0">
          {list.length === 0 ? (
            <p className="text-sm text-gray-500">No partnership bullets to edit.</p>
          ) : (
            list.map((b, index) => (
              <div key={index} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Title</p>
                <p className="text-sm font-semibold text-gray-900 mb-3">{String(b?.title ?? "").trim() || "(untitled)"}</p>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</p>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => wrapBold(index)}
                    className="px-3 py-1.5 text-sm font-medium bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Bold
                  </button>
                </div>
                <textarea
                  ref={(el) => {
                    if (el) taRefs.current[index] = el
                  }}
                  value={descriptions[index] ?? ""}
                  onChange={(e) => {
                    const v = e.target.value
                    setDescriptions((prev) => {
                      const copy = [...prev]
                      copy[index] = v
                      return copy
                    })
                  }}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-full min-h-[100px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  spellCheck
                />
                <div className="mt-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Preview</p>
                  <div className="border border-gray-200 rounded-md p-3 bg-white text-sm prose prose-sm max-w-none">
                    <ReactMarkdown
                      allowedElements={["p", "strong", "em", "br", "ul", "ol", "li"]}
                      unwrapDisallowed
                      skipHtml
                    >
                      {descriptions[index] || " "}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(descriptions)
              onClose()
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
